
import { useMemo, useRef } from "react";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { App, Button, Flex, notification, Tag, Tooltip, Typography } from "antd";
import { ColumnsType } from "antd/es/table";

import Dict from "@/components/Dict";
import { DictEnum } from "@/types/dict";
import { CreditLevelSelect, CurrencySelect, CountrySelect, DepartmentSelect } from "@/components/Selector";
import { SaleItemState, SaleOrderItemState } from "@/types/sales";
import SText from "@/styles/SText";
import { dateFormat, priceFormat } from "@/utils/format";
import useCopy from "@/hooks/useCopy";

import iconFolder from "@/assets/images/fileIcon/folder.svg";
import iconGenerate from "@/assets/images/action/generate.svg";
import { CopyFilled, DeleteFilled, RetweetOutlined } from "@ant-design/icons";
import usePage from "@/layout/hooks/usePage";
import { PageId, PageType } from "@/types/page";
import useSaleItemLabel from "@/pages/Sales/hooks/useSaleItemLabel";
import { numFormmater } from "@/pages/Requirement/util";
import WMSAvatar from "@/styles/WMSAvatar";
import { getUserAvatarTip, getUserName } from "@/utils/user";
import { divided, noRateAmount } from "@/utils/calc";

type UseOrderColumn = (props: {
  onDetail: (params: SaleOrderItemState | null) => void;
  onDel: (id: string) => void;
  onOpenTab: (type: 'doc', item: SaleOrderItemState) => void;
  onOpenFinanceRequire: (params: SaleOrderItemState | null) => void;
  onOut: (record: SaleItemState) => void;
  onOpenRequirement: (record: SaleOrderItemState | null) => void;
  openSwap: (record: SaleOrderItemState | null) => void;
  openSwapPur?: (record: SaleOrderItemState[]) => void;
  pageIdOverride?: PageId;
  pageTypeOverride?: PageType;
}) => ColumnsType<SaleItemState>

// 勾选只改变 selection 列；业务行对象没变时跳过业务单元格渲染。
const shouldSaleOrderCellUpdate = (record: SaleOrderItemState, prevRecord: SaleOrderItemState) => record !== prevRecord;

const useOrderColumn: UseOrderColumn = ({
  onDetail,
  onDel,
  onOpenTab,
  onOpenFinanceRequire,
  onOut,
  onOpenRequirement,
  openSwap,
  openSwapPur,
  pageIdOverride,
  pageTypeOverride,
}) => {
  const { t } = useTranslation();
  const { modal } = App.useApp();
  const { onCopy } = useCopy();
  const { pageId, pageType } = usePage();
  // 允许仓储场景复用销售订单列配置，而不是强依赖当前路由页面。
  const resolvedPageId = pageIdOverride ?? pageId;
  const resolvedPageType = pageTypeOverride ?? pageType;
  const isOfficialOrder = resolvedPageId === PageId.SALE_OFFICIAL || resolvedPageId === PageId.F_SALE_OFFICIAL;
  const { getLabelShow } = useSaleItemLabel();
  // 先把业务隐藏规则计算成 boolean，避免把不稳定的 getLabelShow 函数放进 columns 依赖。
  const hideCustomerLabel = getLabelShow('customer');
  const hideCustomerModelLabel = getLabelShow('customerModel');
  const hidePriceLabel = getLabelShow('price');
  const hideDetailAction = getLabelShow('detail');
  // 列配置保持稳定，点击事件从 ref 读取最新回调，避免父组件重渲染时整套 columns 重建。
  const handlersRef = useRef({
    onCopy,
    onDel,
    onDetail,
    onOpenFinanceRequire,
    onOpenRequirement,
    onOpenTab,
    onOut,
    openSwap,
    openSwapPur,
  });

  handlersRef.current = {
    onCopy,
    onDel,
    onDetail,
    onOpenFinanceRequire,
    onOpenRequirement,
    onOpenTab,
    onOut,
    openSwap,
    openSwapPur,
  };

  // columns 只在页面类型、语言或隐藏规则变化时重建；勾选状态变化不应触发业务列重建。
  const columns: ColumnsType<SaleOrderItemState> = useMemo(() => [
    {
      title: <SText text={t("sale.orderNumber")} />,
      key: "order.number",
      dataIndex: ["order", "number"],
      width: 240,
      fixed: 'left',
      shouldCellUpdate: shouldSaleOrderCellUpdate,
      sorter: (a, b) => {
        const valA = a?.number ?? "";
        const valB = b?.number ?? "";
        return valA.localeCompare(valB);
      },
      render: (_, item) => (
        <Flex gap={4} align='center'>
          <Typography.Link
            style={{ color: '#1890ff', cursor: 'pointer' }}
            onClick={() => {
              handlersRef.current.onDetail(item);
            }}
          >{item?.number ?? ""}</Typography.Link>
          {!!item.sourceOrder && <Tag color="red">{t('requirement.swapSale')}</Tag>}
        </Flex>
      ),
    },
    {
      title: <SText text={t("sale.orderDate")} />,
      key: "createdAt",
      dataIndex: "createdAt",
      width: 120,
      fixed: 'left',
      shouldCellUpdate: shouldSaleOrderCellUpdate,
      render: (_, item) => <SText text={item?.createdAt ? dateFormat(dayjs(item?.createdAt)) : ''} style={{ width: "100%" }} />,
    },
    // {
    //   title: <SText text={t("sale.orderState")} />,
    //   dataIndex: "preApproval",
    //   width: 140,
    //   render: (_, item) => (
    //     <Tag color={item?.preApproval ? 'green' : 'red'}>{item?.preApproval ? t("sale.passed") : t("sale.notApproval")}</Tag>
    //   ),
    // },
    {
      title: <SText text={t("sale.innerNumber")} />,
      key: "order.innerNumber",
      dataIndex: ["order", "innerNumber"],
      width: 120,
      fixed: 'left',
      shouldCellUpdate: shouldSaleOrderCellUpdate,
      sorter: (a, b) => {
        const valA = a?.innerNumber ?? "";
        const valB = b?.innerNumber ?? "";
        return valA.localeCompare(valB);
      },
      render: (_, item) => (
        <SText text={item?.innerNumber ?? ""} style={{ width: "100%" }} />
      ),
    },
    {
      title: <SText text={t("sale.shortName")} />,
      key: "shortName",
      dataIndex: "shortName",
      width: 80,
      hidden: hideCustomerLabel,
      shouldCellUpdate: shouldSaleOrderCellUpdate,
      render: (_, item) => (
        <SText text={item?.shortName ?? ""} style={{ width: "100%" }} />
      ),
    },
    {
      title: <SText text={t("product.code")} />,
      key: "product.code",
      dataIndex: ["product", "code"],
      width: 120,
      shouldCellUpdate: shouldSaleOrderCellUpdate,
      sorter: (a, b) => {
        const valA = a?.items?.[0]?.product?.code ?? "";
        const valB = b?.items?.[0]?.product?.code ?? "";
        return valA.localeCompare(valB);
      },
      render: (_, item) => <SText text={item.productId ? (item?.items?.[0]?.product?.code ?? '') : ''} style={{ width: "100%" }} />,
    },
    {
      title: <SText text={t("sale.reqDeliDate")} />,
      key: "reqDeliDate",
      dataIndex: "reqDeliDate",
      width: 120,
      shouldCellUpdate: shouldSaleOrderCellUpdate,
      render: (_, item) => <SText text={item?.reqDeliDate ? dateFormat(dayjs(item?.reqDeliDate)) : ''} style={{ width: "100%" }} />,
    },
    {
      title: <SText text={t("sale.customerModel")} />,
      key: "customerModel",
      dataIndex: "customerModel",
      width: 120,
      hidden: hideCustomerModelLabel,
      shouldCellUpdate: shouldSaleOrderCellUpdate,
      render: (_, item) => <SText text={item?.customerModel ?? ''} style={{ width: "100%" }} />,
    },
    {
      title: <SText text={t("sale.mfgr")} />,
      key: "mfgr",
      dataIndex: "mfgr",
      width: 120,
      shouldCellUpdate: shouldSaleOrderCellUpdate,
      render: (_, item) => <SText text={item?.mfgr ?? ''} style={{ width: "100%" }} />,
    },
    {
      title: <SText text={t("sale.quantity")} />,
      key: "quantity",
      dataIndex: "quantity",
      width: 120,
      shouldCellUpdate: shouldSaleOrderCellUpdate,
      render: (_, item) => <SText text={numFormmater((Number(item?.items?.[0]?.quantity) + Number(item?.items?.[0]?.bquantity)) || 0)} style={{ width: "100%" }} />,
    },
    {
      title: <SText text={t("sale.smtStartDate")} />,
      key: "smtStartDate",
      dataIndex: "smtStartDate",
      width: 120,
      shouldCellUpdate: shouldSaleOrderCellUpdate,
      render: (_, item) => <SText text={item?.smtStartDate ? dateFormat(dayjs(item?.smtStartDate)) : ''} style={{ width: "100%" }} />,
    },
    {
      title: <SText text={t("sale.preFinishDate")} />,
      key: "preFinishDate",
      dataIndex: "preFinishDate",
      width: 120,
      shouldCellUpdate: shouldSaleOrderCellUpdate,
      render: (_, item) => <SText text={item?.preFinishDate ? dateFormat(dayjs(item?.preFinishDate)) : ''} style={{ width: "100%" }} />,
    },
    {
      title: <SText text={t("sale.taxPrice")} />,
      key: "price",
      dataIndex: "price",
      width: 120,
      hidden: hidePriceLabel,
      shouldCellUpdate: shouldSaleOrderCellUpdate,
      render: (_, item) => <SText text={priceFormat(item?.items?.[0]?.price ?? '')} style={{ width: "100%" }} />,
    },
    {
      title: <SText text={t("purchase.unRatePrice")} />,
      key: "unRatePrice",
      width: 110,
      render: (_, item) => {
        const taxRate = Number(item.taxRate) ?? 0;
        let noRateInfo = '';
        if (taxRate !== 0) {
          noRateInfo = priceFormat(noRateAmount(item?.items?.[0]?.price ?? "", divided(taxRate + '', "100")))
        } else noRateInfo = item?.items?.[0]?.price ?? "";
        return (
          <SText text={noRateInfo} />
        );
      },
    },
    {
      title: <SText text={t("sale.currency")} />,
      key: "currency",
      dataIndex: "currency",
      hidden: hidePriceLabel,
      width: 100,
      shouldCellUpdate: shouldSaleOrderCellUpdate,
      render: (val, item) => (
        <CurrencySelect value={item?.currency || ""} readOnly />
      ),
    },
    {
      title: <SText text={t("purchase.rateAmount")} />,
      key: "totalRealAmount",
      dataIndex: "totalRealAmount",
      hidden: hidePriceLabel,
      width: 140,
      shouldCellUpdate: shouldSaleOrderCellUpdate,
      render: (_, item) => (
        <SText text={numFormmater(Number(item?.totalRealAmount ?? ""))} style={{ width: "100%" }} />
      ),
    },
    {
      title: <SText text={t("purchase.amount")} />,
      key: "totalAmount",
      dataIndex: "totalAmount",
      hidden: hidePriceLabel,
      width: 140,
      shouldCellUpdate: shouldSaleOrderCellUpdate,
      render: (_, item) => (
        <SText text={numFormmater(Number(item?.totalAmount ?? ""))} style={{ width: "100%" }} />
      ),
    },
    {
      title: <SText text={t("sale.tradeTerm")} />,
      key: "tradeTerm",
      dataIndex: "tradeTerm",
      hidden: hidePriceLabel,
      width: 140,
      shouldCellUpdate: shouldSaleOrderCellUpdate,
      render: (val, item) => (
        <Dict type={DictEnum.SALE_TRADE_TERM} value={item?.tradeTerm} readOnly />
      ),
    },
    {
      title: <SText text={t("sale.payMethod")} />,
      key: "payMethod",
      dataIndex: "payMethod",
      hidden: hidePriceLabel,
      width: 100,
      shouldCellUpdate: shouldSaleOrderCellUpdate,
      render: (val, item) => (
        <Dict type={DictEnum.SALE_PAY_METHOD} value={item?.payMethod} readOnly />
      ),
    },
    {
      title: <SText text={t("sale.depositRate")} />,
      key: "depositRate",
      dataIndex: "depositRate",
      hidden: hidePriceLabel,
      width: 80,
      shouldCellUpdate: shouldSaleOrderCellUpdate,
      render: (_, item) => (
        <SText
          text={item?.depositRate ?? ""}
          style={{ width: "100%" }}
          suffix="%"
        />
      ),
    },
    {
      title: <SText text={t("sale.specialReq")} />,
      key: "specialReq",
      dataIndex: "specialReq",
      width: 120,
      shouldCellUpdate: shouldSaleOrderCellUpdate,
      render: (_, item) => <SText text={item?.specialReq ?? ''} style={{ width: "100%" }} />,
    },
    {
      title: <SText text={t("sale.reRemark")} />,
      key: "remark",
      dataIndex: "remark",
      width: 120,
      shouldCellUpdate: shouldSaleOrderCellUpdate,
      render: (_, item) => <SText text={item?.items?.[0]?.remark ?? ''} style={{ width: "100%" }} />,
    },
    {
      title: <SText text={t("requirement.inCount")} />,
      key: "inCount",
      dataIndex: "inCount",
      width: 100,
      hidden: !isOfficialOrder,
      shouldCellUpdate: shouldSaleOrderCellUpdate,
      render: (_, item) => {
        const inCount = item?.items?.[0]?.inCount;
        return <SText text={numFormmater(Number(inCount ?? ""))} style={{ width: "100%" }} />;
      },
    },
    {
      title: <SText text={t("requirement.outCount")} />,
      key: "outCount",
      dataIndex: "outCount",
      width: 100,
      hidden: !isOfficialOrder,
      shouldCellUpdate: shouldSaleOrderCellUpdate,
      render: (_, item) => {
        const outCount = item?.items?.[0]?.outCount;
        return <SText text={numFormmater(Number(outCount ?? ""))} style={{ width: "100%" }} />;
      },
    },
    {
      title: t("sale.proInfo"),
      key: "productInfo",
      hidden: isOfficialOrder,
      children: [
        {
          title: <SText text={t("product.name")} />,
          key: "proInfoName",
          dataIndex: "proInfoName",
          width: 140,
          shouldCellUpdate: shouldSaleOrderCellUpdate,
          render: (_, item) => (
            !!item.productId && <SText text={item?.items?.[0]?.product?.name ?? ""} style={{ width: "100%" }} />
          ),
        },
        {
          title: <SText text={t("product.uom")} />,
          key: "proInfoMsmUnit",
          dataIndex: "proInfoMsmUnit",
          width: 140,
          shouldCellUpdate: shouldSaleOrderCellUpdate,
          render: (_, item) => (
            !!item.productId && <SText text={item?.items?.[0]?.product?.uom?.toLocaleUpperCase() ?? ""} style={{ width: "100%" }} />
          ),
        },
        {
          title: <SText text={t("product.brand1")} />,
          key: "proInfoBrand1",
          dataIndex: "proInfoBrand1",
          width: 140,
          shouldCellUpdate: shouldSaleOrderCellUpdate,
          render: (_, item) => (
            !!item.productId && <SText text={item?.items?.[0]?.product?.model?.toLocaleUpperCase() ?? ""} style={{ width: "100%" }} />
          ),
        },
        {
          title: <SText text={t("product.brand2")} />,
          key: "proInfoBrand2",
          dataIndex: "proInfoBrand2",
          width: 140,
          shouldCellUpdate: shouldSaleOrderCellUpdate,
          render: (_, item) => (
            !!item.productId && <SText text={item?.items?.[0]?.product?.t1?.toLocaleUpperCase() ?? ""} style={{ width: "100%" }} />
          ),
        },
        {
          title: <SText text={t("common.desc")} />,
          key: "proInfoDesc",
          dataIndex: "proInfoDesc",
          width: 140,
          shouldCellUpdate: shouldSaleOrderCellUpdate,
          render: (_, item) => (
            !!item.productId && <SText text={item?.items?.[0]?.product?.desc ?? ""} style={{ width: "100%" }} />
          ),
        },
        {
          title: <SText text={t("product.cbm")} />,
          key: "proInfoCbm",
          dataIndex: "proInfoCbm",
          width: 140,
          shouldCellUpdate: shouldSaleOrderCellUpdate,
          render: (_, item) => (
            !!item.productId && <SText text={item?.items?.[0]?.product?.cbm ?? ""} style={{ width: "100%" }} />
          ),
        },
      ],
    },
    {
      title: t("sale.orderInfo"),
      key: "orderInfo",
      hidden: hideCustomerLabel,
      children: [
        {
          title: <SText text={t("sale.receivingUnit")} />,
          key: "receiver",
          dataIndex: "receiver",
          hidden: hideCustomerLabel,
          width: 140,
          shouldCellUpdate: shouldSaleOrderCellUpdate,
          render: (_, item) => (
            <SText text={item?.receiver ?? ""} style={{ width: "100%" }} />
          ),
        },
        {
          title: <SText text={t("sale.receivingContact")} />,
          key: "receiverContact",
          dataIndex: "receiverContact",
          hidden: hideCustomerLabel,
          width: 140,
          shouldCellUpdate: shouldSaleOrderCellUpdate,
          render: (_, item) => (
            <SText text={item?.receiverContact ?? ""} style={{ width: "100%" }} />
          ),
        },
        {
          title: <SText text={t("sale.receivingContactMobile")} />,
          key: "receiverMobile",
          dataIndex: "receiverMobile",
          hidden: hideCustomerLabel,
          width: 140,
          shouldCellUpdate: shouldSaleOrderCellUpdate,
          render: (_, item) => (
            <SText text={item?.receiverMobile ?? ""} style={{ width: "100%" }} />
          ),
        },
        {
          title: <SText text={t("sale.receivingAddress")} />,
          key: "receiverAddress",
          dataIndex: "receiverAddress",
          hidden: hideCustomerLabel,
          width: 140,
          shouldCellUpdate: shouldSaleOrderCellUpdate,
          render: (_, item) => (
            <SText text={item?.receiverAddress ?? ""} style={{ width: "100%" }} />
          ),
        },
        // 真实价格隐藏
        // {
        //   title: <SText text={t("sale.realPrice")} />,
        //   dataIndex: "realAmount",
        //   width: 140,
        //   render: (_, item) => (
        //     <SText text={item?.realAmount ?? ""} style={{ width: "100%" }} />
        //   ),
        // },
        {
          title: <SText text={t("sale.revisePrice")} />,
          key: "extPrice",
          dataIndex: "extPrice",
          hidden: hideCustomerLabel,
          width: 140,
          shouldCellUpdate: shouldSaleOrderCellUpdate,
          render: (_, item) => <SText text={item?.extPrice ?? ""} style={{ width: "100%" }} />,
        },
        {
          title: <SText text={t("sale.actualPrice")} />,
          key: "curPrice",
          dataIndex: "curPrice",
          hidden: hideCustomerLabel,
          width: 140,
          shouldCellUpdate: shouldSaleOrderCellUpdate,
          render: (_, item) => {
            return <SText text={numFormmater(parseFloat(item?.totalAmount ? item?.totalAmount : '0') + parseFloat(item?.extPrice ? item?.extPrice : '0'))} style={{ width: "100%" }} />
          },
        },
        {
          title: <SText text={t("sale.shippingMethod")} />,
          key: "shippingMethod",
          dataIndex: "shippingMethod",
          hidden: hideCustomerLabel,
          width: 140,
          shouldCellUpdate: shouldSaleOrderCellUpdate,
          render: (_, item) => (
            <Dict type={DictEnum.SALE_SHIPPING_METHOD} value={item?.shippingMethod ?? ""} readOnly />
          ),
        },
      ],
    },
    {
      title: t("sale.customerInfo"),
      key: "customerInfo",
      children: [
        {
          title: <SText text={t("sale.code")} />,
          key: "code",
          dataIndex: "code",
          width: 80,
          hidden: hideCustomerLabel,
          shouldCellUpdate: shouldSaleOrderCellUpdate,
          render: (_, item) => <SText text={item?.code} style={{ width: "100%" }} />,
        },
        {
          title: <SText text={t("sale.name1")} />,
          key: "name1",
          dataIndex: "name1",
          width: 180,
          hidden: hideCustomerLabel,
          shouldCellUpdate: shouldSaleOrderCellUpdate,
          render: (_, item) => (
            <SText text={item?.name1 ?? ""} style={{ width: "100%" }} />
          ),
        },
        {
          title: <SText text={t("sale.name2")} />,
          key: "name2",
          dataIndex: "name2",
          width: 180,
          hidden: hideCustomerLabel,
          shouldCellUpdate: shouldSaleOrderCellUpdate,
          render: (_, item) => (
            <SText text={item?.name2} style={{ width: "100%" }} />
          ),
        },
        {
          title: <SText text={t("sale.country")} />,
          key: "country",
          dataIndex: "country",
          width: 100,
          hidden: hideCustomerLabel,
          shouldCellUpdate: shouldSaleOrderCellUpdate,
          render: (val, item) => (
            <CountrySelect value={item?.country} readOnly />
          ),
        },
        {
          title: <SText text={t("sale.address")} />,
          key: "address",
          dataIndex: "address",
          width: 240,
          hidden: hideCustomerLabel,
          shouldCellUpdate: shouldSaleOrderCellUpdate,
          render: (_, item) => (
            <SText text={item?.address ?? ""} style={{ width: "100%" }} />
          ),
        },
        {
          title: <SText text={t("sale.zipCode")} />,
          key: "zipCode",
          dataIndex: "zipCode",
          width: 70,
          hidden: hideCustomerLabel,
          shouldCellUpdate: shouldSaleOrderCellUpdate,
          render: (_, item) => (
            <SText text={item?.zipCode ?? ""} style={{ width: "100%" }} />
          ),
        },
        {
          title: <SText text={t("sale.email")} />,
          key: "email",
          dataIndex: "email",
          width: 160,
          hidden: hideCustomerLabel,
          shouldCellUpdate: shouldSaleOrderCellUpdate,
          render: (_, item) => (
            <SText text={item?.email ?? ""} style={{ width: "100%" }} />
          ),
        },
        {
          title: <SText text={t("sale.contact")} />,
          key: "contact",
          dataIndex: "contact",
          width: 80,
          hidden: hideCustomerLabel,
          shouldCellUpdate: shouldSaleOrderCellUpdate,
          render: (_, item) => (
            <SText text={item?.contact ?? ""} style={{ width: "100%" }} />
          ),
        },
        {
          title: <SText text={t("sale.phone")} />,
          key: "phone",
          dataIndex: "phone",
          width: 100,
          hidden: hideCustomerLabel,
          shouldCellUpdate: shouldSaleOrderCellUpdate,
          render: (_, item) => (
            <SText text={item?.phone ?? ""} style={{ width: "100%" }} />
          ),
        },
        {
          title: <SText text={t("sale.fax")} />,
          key: "fax",
          dataIndex: "fax",
          width: 100,
          hidden: hideCustomerLabel,
          shouldCellUpdate: shouldSaleOrderCellUpdate,
          render: (_, item) => (
            <SText text={item?.fax ?? ""} style={{ width: "100%" }} />
          ),
        },
        {
          title: <SText text={t("sale.mobile")} />,
          key: "mobile",
          dataIndex: "mobile",
          width: 120,
          hidden: hideCustomerLabel,
          shouldCellUpdate: shouldSaleOrderCellUpdate,
          render: (_, item) => (
            <SText text={item?.mobile ?? ""} style={{ width: "100%" }} />
          ),
        },
        {
          title: <SText text={t("sale.account1")} />,
          key: "account1",
          dataIndex: "account1",
          width: 180,
          hidden: hideCustomerLabel,
          shouldCellUpdate: shouldSaleOrderCellUpdate,
          render: (_, item) => (
            <SText text={item?.account1 ?? ""} style={{ width: "100%" }} />
          ),
        },
        {
          title: <SText text={t("sale.bank1")} />,
          key: "bank1",
          dataIndex: "bank1",
          width: 140,
          hidden: hideCustomerLabel,
          shouldCellUpdate: shouldSaleOrderCellUpdate,
          render: (_, item) => (
            <SText text={item?.bank1 ?? ""} style={{ width: "100%" }} />
          ),
        },
        {
          title: <SText text={t("sale.swiftCode1")} />,
          key: "swiftCode1",
          dataIndex: "swiftCode1",
          width: 80,
          hidden: hideCustomerLabel,
          shouldCellUpdate: shouldSaleOrderCellUpdate,
          render: (_, item) => (
            <SText text={item?.swiftCode1 ?? ""} style={{ width: "100%" }} />
          ),
        },
        {
          title: <SText text={t("sale.account2")} />,
          key: "account2",
          dataIndex: "account2",
          width: 180,
          hidden: hideCustomerLabel,
          shouldCellUpdate: shouldSaleOrderCellUpdate,
          render: (_, item) => (
            <SText text={item?.account2 ?? ""} style={{ width: "100%" }} />
          ),
        },
        {
          title: <SText text={t("sale.bank2")} />,
          key: "bank2",
          dataIndex: "bank2",
          width: 140,
          hidden: hideCustomerLabel,
          shouldCellUpdate: shouldSaleOrderCellUpdate,
          render: (_, item) => (
            <SText text={item?.bank2 ?? ""} style={{ width: "100%" }} />
          ),
        },
        {
          title: <SText text={t("sale.swiftCode2")} />,
          key: "swiftCode2",
          dataIndex: "swiftCode2",
          width: 100,
          hidden: hideCustomerLabel,
          shouldCellUpdate: shouldSaleOrderCellUpdate,
          render: (_, item) => (
            <SText text={item?.swiftCode2 ?? ""} style={{ width: "100%" }} />
          ),
        },
        {
          title: <SText text={t("sale.saleType")} />,
          key: "saleType",
          dataIndex: "saleType",
          width: 140,
          hidden: hideCustomerLabel,
          shouldCellUpdate: shouldSaleOrderCellUpdate,
          render: (val, item) => (
            <Dict type={DictEnum.SALE_TYPE} value={item?.saleType} readOnly />
          ),
        },
        {
          title: <SText text={t("sale.creditLevel")} />,
          key: "creditLevel",
          dataIndex: "creditLevel",
          width: 80,
          hidden: hideCustomerLabel,
          shouldCellUpdate: shouldSaleOrderCellUpdate,
          render: (val, item) => (
            <CreditLevelSelect value={item?.creditLevel || ""} readOnly />
          ),
        },
        {
          title: <SText text={t("sale.creditLine")} />,
          key: "creditLine",
          dataIndex: "creditLine",
          width: 80,
          hidden: hideCustomerLabel,
          shouldCellUpdate: shouldSaleOrderCellUpdate,
          render: (_, item) => (
            <SText text={item?.creditLine ?? ""} style={{ width: "100%" }} />
          ),
        },
        {
          title: <SText text={t("sale.org")} />,
          key: "orgId",
          dataIndex: "orgId",
          width: 180,
          shouldCellUpdate: shouldSaleOrderCellUpdate,
          render: (val, item) => (
            <DepartmentSelect value={item?.orgId} readOnly style={{ width: "100%" }} />
          ),
        },
        {
          title: <SText text={t("sale.taxRate")} />,
          key: "taxRate",
          dataIndex: "taxRate",
          width: 100,
          hidden: hideCustomerLabel,
          shouldCellUpdate: shouldSaleOrderCellUpdate,
          render: (_, item) => (
            <SText text={item?.taxRate ?? ""} style={{ width: "100%" }} suffix="%" />
          ),
        },
      ],
    },
    // {
    //   title: <SText text={t("order.isSwap")} />,
    //   dataIndex: "sourceOrder",
    //   width: 60,
    //   shouldCellUpdate: shouldSaleOrderCellUpdate,
    //   render: (_, item) => (
    //     !!item.sourceOrder ? <Tag color="green">{t('product.isTrue')}</Tag>
    //       : <Tag color="red">{t('product.isFalse')}</Tag>
    //   ),
    // },
    {
      title: <SText text={t("common.creator")} />,
      key: "creator",
      dataIndex: "creator",
      width: 100,
      render: (_, item) => {
        let userData = item.creator;
        return (
          userData && <Flex>
            <div>
              <WMSAvatar creator={userData} uniChar={userData.id}
                avatarId={userData.avatar}
                size={24} alt={getUserAvatarTip(userData)}
              />
            </div>
            <SText text={getUserName(userData)} />
          </Flex>
        )
      },
    },
    {
      title: <SText text={t("sale.orderState")} />,
      key: "finishState",
      dataIndex: "finishState",
      width: 80,
      fixed: 'right',
      hidden: !isOfficialOrder,
      shouldCellUpdate: shouldSaleOrderCellUpdate,
      render: (_, item) => (
        <Tag color={item.finishState === 1 ? 'success' : 'default'}>
          {item.finishState === 1 ? t("sale.finishedOrder") : t("sale.unfinishedOrder")}
        </Tag>
      ),
    },
    {
      title: <SText text={t("sale.requirement")} />,
      key: "requirement",
      dataIndex: "requirement",
      fixed: "right",
      width: 200,
      hidden: resolvedPageType !== PageType.FINANCE && resolvedPageId !== PageId.SALE_NOT_PASS && !isOfficialOrder,
      shouldCellUpdate: shouldSaleOrderCellUpdate,
      render: (_, item) => (
        item?.requirement ?
          <Typography.Link onClick={() => handlersRef.current.onOpenRequirement(item)}>{item?.requirement?.number}</Typography.Link> :
          <Typography.Text type="secondary">{t("sale.notRequirement")}</Typography.Text>
      ),
    },
    {
      title: <SText text={t("sale.relatedDocuments")} />,
      key: "relatedDocuments",
      dataIndex: "relatedDocuments",
      fixed: "right",
      width: 60,
      shouldCellUpdate: shouldSaleOrderCellUpdate,
      render: (_, item) => (
        <Flex align="center" justify="center" style={{ width: "100%" }}>
          <img
            src={iconFolder}
            alt=""
            onClick={() => handlersRef.current.onOpenTab("doc", item)}
            style={{ cursor: "pointer" }}
          />
        </Flex>
      ),
    },
    {
      title: t("common.opt"),
      // 操作按钮随页面及显示规则变化，切换时重新挂载，避免复用旧按钮。
      key: `operation:${resolvedPageId}:${hideDetailAction}`,
      dataIndex: "operation",
      width: resolvedPageId === PageId.SALE_NOT_PASS ? 160 : 90,
      fixed: "right",
      shouldCellUpdate: shouldSaleOrderCellUpdate,
      render: (val, record) => (
        <Flex gap={6}>
          {!hideDetailAction && <Button type="primary" onClick={() => handlersRef.current.onDetail(record)}>
            {t("common.detail")}
          </Button>}
          {false && <Button type="primary" onClick={() => handlersRef.current.onOut(record?.items?.[0])}>
            {t("warehouse.outbound")}
          </Button>}
          {resolvedPageId && [PageId.SALE_NOT_PASS, PageId.SALE_OFFICIAL, PageId.F_SALE_OFFICIAL].includes(resolvedPageId) && <Tooltip title={t("common.copy")}>
            <Button type="link" icon={<CopyFilled />} onClick={() => {
              if (record.bomFileId) {
                modal.confirm({
                  title: t('common.tip'),
                  content: t('sale.bomCopyTip'),
                  // cancelText: t('sale.bomCopyCancel'),
                  // okText: t('sale.bomCopyYes'),
                  onOk: () => handlersRef.current.onCopy(JSON.stringify({ ...record })),
                  onCancel: () => handlersRef.current.onCopy(JSON.stringify({ ...record, bomFileId: '' })),
                })
              } else handlersRef.current.onCopy(JSON.stringify({ ...record }))
            }} />
          </Tooltip>}
          {resolvedPageId === PageId.MC_NOT_PASS && <Tooltip title={t('requirement.requirementAdd')}>
            <Button type='primary' onClick={() => handlersRef.current.onOpenFinanceRequire(record)} icon={<img width={20} src={iconGenerate} alt="" />} />
          </Tooltip>}
          {resolvedPageId === PageId.SALE_NOT_PASS && <Tooltip title={t("common.del")}>
            <Button type="link" danger icon={<DeleteFilled />} onClick={() => handlersRef.current.onDel(record?.id)} />
          </Tooltip>}
        </Flex>
      ),
    },
    {
      title: t("requirement.swapSale"),
      key: "swap",
      hidden: resolvedPageId !== PageId.SALE_NOT_PASS,
      fixed: "right",
      render: (_, record) => (
        <Flex gap={6}>
          <Tooltip title={t("sale.saleSwap")}>
            <Button type='link' disabled={record.items.length !== 1} onClick={() => {
              if (record.items.length === 1) handlersRef.current.openSwap(record)
              else notification.error({ message: t('sale.notSale'), description: `${record.number}` })
            }}>{t("sale.saleSwap")}</Button>
          </Tooltip>
          <Tooltip title={t("sale.purSwap")}>
            <Button type='text' onClick={() => {
              if (!!!record.customer.wmsApi) notification.error({ message: t('sale.notApi'), description: `${record.customer.shortName ?? ''}` })
              else handlersRef.current.openSwapPur?.([record])
            }}>{t("sale.purSwap")}</Button>
          </Tooltip>
        </Flex>
      )
    }
  ], [t, resolvedPageId, resolvedPageType, isOfficialOrder, hideCustomerLabel, hideCustomerModelLabel, hidePriceLabel, hideDetailAction]);

  // 过滤掉 hidden 为 true 的列，并保持结果引用稳定。
  const visibleColumns = useMemo(() => columns.filter(col => !col.hidden).map(col => {
    const { hidden, ...rest } = col as any;
    // 如果有 children，也要过滤
    if (rest.children) {
      rest.children = rest.children.filter((child: any) => !child.hidden).map((child: any) => {
        const { hidden: childHidden, ...childRest } = child;
        return childRest;
      });
    }
    return rest;
  }), [columns]);

  return visibleColumns;
}

export default useOrderColumn;
